import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

export type SimpleDropdownItem = {
  label: string;
  action?: () => void;
  icon?: ReactNode;
  variant?: "default" | "destructive";
  disabled?: boolean;
};

export type SimpleDropdownProps = {
  items: (SimpleDropdownItem | { type: "separator" })[];
  triggerLabel?: ReactNode;
  align?: ComponentPropsWithoutRef<typeof DropdownMenuContent>["align"];
  side?: ComponentPropsWithoutRef<typeof DropdownMenuContent>["side"];
  onSelect?: (event: Event) => void;
  className?: string;
};

export function SimpleDropdown({
  items,
  triggerLabel,
  align = "end",
  side,
  className,
  onSelect,
}: SimpleDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          {triggerLabel ?? <MoreHorizontal className="size-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side}>
        {items.map((item, index) => {
          if ("type" in item && item.type === "separator") {
            return <DropdownMenuSeparator key={`sep-${index}`} />;
          }
          const menuItem = item as SimpleDropdownItem;
          return (
            <DropdownMenuItem
              key={menuItem.label}
              variant={
                menuItem.variant === "destructive" ? "destructive" : "default"
              }
              disabled={menuItem.disabled}
              onSelect={(event) => {
                onSelect?.(event);
                menuItem.action?.();
              }}
            >
              {menuItem.icon}
              {menuItem.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
